import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SITE_NAME,
  SITE_URL,
  SITE_DESCRIPTION,
  generateMetaDescription,
  generateMetaTitle,
  generateKeywords,
  getOrganizationSchema,
  getWebSiteSchema,
  getFacilitySchema,
  getBreadcrumbSchema,
  getFAQSchema,
  getFacilityListSchema,
  getCanonicalUrl
} from '@/utils/seoConfig';

// Ana SEO Bileşeni
const SEOHead = ({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage,
  ogType = 'website',
  noindex = false,
  facility = null,
  facilities = [],
  breadcrumbs = [],
  city = null,
  district = null,
  showFAQ = false,
  children
}) => {
  // Varsayılan değerler
  const pageTitle = title || `${SITE_NAME} - Spor Tesisi Kiralama Platformu`;
  const pageDescription = description || SITE_DESCRIPTION;
  const pageKeywords = keywords || generateKeywords(facility, city, district);
  const canonicalUrl = canonicalPath ? getCanonicalUrl(canonicalPath) : SITE_URL;
  const pageImage = ogImage || `${SITE_URL}/logo.webp`;

  // Schema.org JSON-LD verileri
  const schemas = [];
  
  // Her sayfada Organization ve WebSite schema
  schemas.push(getOrganizationSchema());
  schemas.push(getWebSiteSchema());
  
  // Breadcrumb varsa ekle
  if (breadcrumbs.length > 0) {
    schemas.push(getBreadcrumbSchema(breadcrumbs));
  }
  
  // Tesis detay sayfası için
  if (facility) {
    const facilitySchema = getFacilitySchema(facility);
    if (facilitySchema) {
      schemas.push(facilitySchema);
    }
  }
  
  // Tesis listesi için
  if (facilities.length > 0) {
    schemas.push(getFacilityListSchema(facilities, city, district));
  }
  
  // FAQ sayfası için
  if (showFAQ) {
    schemas.push(getFAQSchema());
  }

  return (
    <Helmet>
      {/* Temel Meta Etiketleri */}
      <html lang="tr" />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content="TesisKirala.com" />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="googlebot" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang */}
      <link rel="alternate" hreflang="tr" href={canonicalUrl} />
      <link rel="alternate" hreflang="x-default" href={canonicalUrl} />
      
      {/* Open Graph Meta Etiketleri */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:locale" content="tr_TR" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@tesiskirala" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* Ek Meta Etiketleri */}
      <meta name="geo.region" content="TR" />
      <meta name="geo.placename" content={city || 'Türkiye'} />
      <meta name="language" content="Turkish" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="general" />
      
      {/* Mobile */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="theme-color" content="#059669" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      
      {/* JSON-LD Structured Data */}
      {schemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
      
      {children}
    </Helmet>
  );
};

// Ana Sayfa SEO
export const HomePageSEO = () => (
  <SEOHead
    title="TesisKirala.com | Halı Saha, Spor Salonu Kiralama | Online Rezervasyon"
    description="Türkiye'nin en kolay spor tesisi kiralama platformu. Halı saha, spor salonu, voleybol sahası, basketbol sahası kirala. Online rezervasyon, anında onay!"
    canonicalPath="/"
    showFAQ={true}
    breadcrumbs={[
      { name: 'Ana Sayfa' }
    ]}
  />
);

// Tesisler Listesi Sayfası SEO
export const FacilitiesPageSEO = ({ facilities = [], city, district, facilityType }) => {
  let title = 'Spor Tesisleri | Saha Kirala | TesisKirala.com';
  let description = 'Türkiye genelinde kiralık spor tesisleri. Halı saha, spor salonu, voleybol ve basketbol sahası kiralama.';
  
  if (city && district) {
    title = `${district}, ${city} Spor Tesisleri | Saha Kirala | TesisKirala.com`;
    description = `${district}, ${city} bölgesinde kiralık spor tesisleri. Halı saha, spor salonu kiralama. Online rezervasyon yapın!`;
  } else if (city) {
    title = `${city} Spor Tesisleri | Saha Kirala | TesisKirala.com`;
    description = `${city} şehrinde kiralık spor tesisleri. Halı saha, spor salonu, voleybol sahası kiralama.`;
  }
  
  if (facilityType) {
    const typeNames = {
      hali_saha: 'Halı Saha',
      kapali_spor_salonu: 'Spor Salonu',
      voleybol_sahasi: 'Voleybol Sahası',
      basketbol_sahasi: 'Basketbol Sahası'
    };
    const typeName = typeNames[facilityType] || 'Spor Tesisi';
    title = `${typeName} Kirala${city ? ` ${city}` : ''} | TesisKirala.com`;
    description = `${city ? city + ' ' : ''}${typeName.toLowerCase()} kiralama. En uygun fiyatlarla ${typeName.toLowerCase()} kirala, online rezervasyon yap!`;
  }

  const breadcrumbs = [
    { name: 'Ana Sayfa', url: '/' },
    { name: 'Tesisler', url: '/tesisler' }
  ];
  
  if (city) {
    breadcrumbs.push({ name: city, url: `/tesisler?city=${encodeURIComponent(city)}` });
  }
  if (district) {
    breadcrumbs.push({ name: district });
  }

  return (
    <SEOHead
      title={title}
      description={description}
      canonicalPath="/tesisler"
      facilities={facilities}
      city={city}
      district={district}
      breadcrumbs={breadcrumbs}
    />
  );
};

// Tesis Detay Sayfası SEO
export const FacilityDetailPageSEO = ({ facility }) => {
  if (!facility) return null;

  const title = generateMetaTitle(facility);
  const description = generateMetaDescription(facility);
  
  const breadcrumbs = [
    { name: 'Ana Sayfa', url: '/' },
    { name: 'Tesisler', url: '/tesisler' },
    { name: facility.city, url: `/tesisler?city=${encodeURIComponent(facility.city)}` },
    { name: facility.district, url: `/tesisler?city=${encodeURIComponent(facility.city)}&district=${encodeURIComponent(facility.district)}` },
    { name: facility.name }
  ];

  return (
    <SEOHead
      title={title}
      description={description}
      canonicalPath={`/tesis/${facility.facility_id}`}
      ogType="place"
      ogImage={facility.images?.[0]}
      facility={facility}
      city={facility.city}
      district={facility.district}
      breadcrumbs={breadcrumbs}
    />
  );
};

// Hakkımızda Sayfası SEO
export const AboutPageSEO = () => (
  <SEOHead
    title="Hakkımızda | TesisKirala.com - Spor Tesisi Kiralama Platformu"
    description="TesisKirala.com hakkında bilgi alın. Türkiye'nin en kolay spor tesisi kiralama platformu. Misyonumuz, vizyonumuz ve hikayemiz."
    canonicalPath="/hakkimizda"
    breadcrumbs={[
      { name: 'Ana Sayfa', url: '/' },
      { name: 'Hakkımızda' }
    ]}
  />
);

// İletişim Sayfası SEO
export const ContactPageSEO = () => (
  <SEOHead
    title="İletişim | TesisKirala.com - Bize Ulaşın"
    description="TesisKirala.com ile iletişime geçin. Sorularınız, önerileriniz ve işletme başvurularınız için bize ulaşın."
    canonicalPath="/iletisim"
    breadcrumbs={[
      { name: 'Ana Sayfa', url: '/' },
      { name: 'İletişim' }
    ]}
  />
);

// Giriş Sayfası SEO
export const LoginPageSEO = () => (
  <SEOHead
    title="Giriş Yap | TesisKirala.com"
    description="TesisKirala.com hesabınıza giriş yapın. Spor tesisi kiralama, rezervasyon takibi ve daha fazlası."
    canonicalPath="/giris"
    noindex={true}
  />
);

// Kullanıcı Dashboard SEO
export const UserDashboardSEO = () => (
  <SEOHead
    title="Hesabım | TesisKirala.com"
    description="TesisKirala.com hesabınızı yönetin. Rezervasyonlarınızı görüntüleyin, geçmiş kiralamaları inceleyin."
    canonicalPath="/hesabim"
    noindex={true}
  />
);

// İşletmeci Dashboard SEO
export const OperatorDashboardSEO = () => (
  <SEOHead
    title="İşletmeci Paneli | TesisKirala.com"
    description="TesisKirala.com işletmeci paneli. Tesislerinizi yönetin, rezervasyonları takip edin, kazançlarınızı görün."
    canonicalPath="/isletmeci"
    noindex={true}
  />
);

// Admin Dashboard SEO
export const AdminDashboardSEO = () => (
  <SEOHead
    title="Admin Paneli | TesisKirala.com"
    description="TesisKirala.com admin paneli. Sistem yönetimi, tesis onayları, kullanıcı yönetimi."
    canonicalPath="/admin"
    noindex={true}
  />
);

// KVKK Sayfası SEO
export const KVKKPageSEO = () => (
  <SEOHead
    title="KVKK Aydınlatma Metni | TesisKirala.com"
    description="TesisKirala.com Kişisel Verilerin Korunması Kanunu (KVKK) Aydınlatma Metni. Kişisel verilerinizin nasıl işlendiği hakkında bilgi alın."
    canonicalPath="/kvkk"
    breadcrumbs={[
      { name: 'Ana Sayfa', url: '/' },
      { name: 'KVKK Aydınlatma Metni' }
    ]}
  />
);

export default SEOHead;
